import { CircularProgress, Grid, Typography } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { EventDto } from '../../../services/api/sharedTypes/Event'
import ScreenLoadingPlaceholder from '../../../components/ScreenLoadingPlaceholder'
import { displayMessage } from '../../../services/redux/snackbar/snackbar-actions'
import { EventManager } from '../../../services/api/EventManager'
import { useAppDispatch } from '../../../hooks/reduxHooks'
import EventBasicInformation from '../../../components/event//EventBasicInformation'
import CarrierTable from '../../../components/event/tables/CarrierTable'
import BrigadierTable from '../../../components/event/tables/BrigadierTable'
import ProductTable from '../../../components/event/tables/ProductTable'
import { ProductManager } from '../../../services/api/ProductManager'
import {
    ContractorBriefDto,
    ContractorDto,
    ContractorOfEventDto,
    ContractorType,
} from '../../../services/api/sharedTypes/Contractor'
import { ContractorManager } from '../../../services/api/ContractorManager'
import { getEmptyEventObject } from '../../../utils/event-utils'
import { IdNameDto, OrderState, PaymentState } from '../../../services/api/sharedTypes/Common'
import { RouteConstants } from '../../../navigation/navigation-types'
import { setSaveState } from '../../../services/redux/savestate/savestate-actions'
import CustomContainer from '../../../components/common/CustomContainer'
import { getTimeSpanFromFormattedTimeSpan } from '../../../utils/global-utils'
import { getUsedProducts, ProductQuantities } from '../../../utils/product-utils'
import BottomBar from '../../../components/common/BottomBar'

const AddEvent: React.FC = () => {
    const navigate = useNavigate()
    const dispatch = useAppDispatch()

    const [loadingData, setLoadingData] = useState<boolean>(false)
    const [loadingTableData, setLoadingTableData] = useState<boolean>(false)
    const [loadingLookupData, setLoadingLookupData] = useState<boolean>(false)

    const [event, setEvent] = useState<EventDto | undefined>(getEmptyEventObject())

    const [productQuantities, setProductQuantities] = useState<ProductQuantities[]>([])
    const [areProductQuantitiesLoading, setAreProductQuantitiesLoading] = useState<boolean>(false)

    const [contractorList, setContractorList] = useState<ContractorBriefDto[]>()

    const [lookupProducts, setLookupProducts] = useState<IdNameDto[]>()
    const [lookupCarriers, setLookupCarriers] = useState<IdNameDto[]>()
    const [lookupBrigadiers, setLookupBrigadiers] = useState<IdNameDto[]>()

    const canGoBack =
        event?.name ||
            event?.notesForBrigadiers ||
            event?.address.city ||
            event?.address.street ||
            event?.address.zip ||
            event?.clientId ||
            event?.contactPerson ||
            (event?.contractors && event.contractors.length > 0) ||
            (event?.products && event.products.length > 0) ||
            event?.description ||
            event?.orderState !== OrderState.Asked ||
            event?.paymentState !== PaymentState.NotSet
            ? false
            : true

    useEffect(() => {
        dispatch(setSaveState(canGoBack))
    }, [canGoBack, dispatch])

    // gets total quantity of products and calculates used quantity of products. All quantities are set in productQuantities state in format {productId, productName, usedQuantity, totalQuantity}
    const getProductQuantities = async () => {
        if (!event) return
        setProductQuantities([])
        event.products.forEach(async (product) => {
            if (!product.productId || product.quantity <= 0) return
            const eventsOfProduct = await getEventsOfProduct(product.productId, event.startsAt, event.endsAt)
            if (!eventsOfProduct) return
            const usedQuantity =
                eventsOfProduct.events
                    .filter((localProduct) => localProduct.eventId !== event.id)
                    .filter((localProduct) => localProduct.isConfirmed)
                    .reduce((acc, localProduct) => acc + localProduct.quantity, 0) ?? 0
            const totalQuantity = eventsOfProduct.productTotalQuantity ?? 0
            setProductQuantities((prev) => [
                ...prev,
                { productId: product.productId, productName: product.productName, usedQuantity, totalQuantity },
            ])
        })
    }

    useEffect(() => {
        const getContractors = async () => {
            setLoadingTableData(true)
            try {
                const contractorsRes = await ContractorManager.getManager().getContractorListAsync()
                if (!contractorsRes) throw new Error('Pracovníci nenalezeni')
                setContractorList(contractorsRes.contractors)
            } catch (error) {
                dispatch(displayMessage({ message: error, type: 'error' }))
                setLoadingTableData(false)
            }
            setLoadingTableData(false)
        }

        getContractors()
    }, [setLoadingTableData])

    useEffect(() => {
        const getLookupData = async () => {
            setLoadingLookupData(true)
            try {
                const lookupProductsRes = await ProductManager.getManager().getProductsLookupAsync()
                if (!lookupProductsRes) throw new Error('Produkty nenalezeny')
                setLookupProducts(lookupProductsRes.items)
                const lookupCarriersRes = await ContractorManager.getManager().getCarriersLookupAsync()
                if (!lookupCarriersRes) throw new Error('Dopravci nenalezeni')
                const lookupBrigadiersRes = await ContractorManager.getManager().getBrigadiersLookupAsync()
                if (!lookupBrigadiersRes) throw new Error('Brigádníci nenalezeni')
                setLookupCarriers(lookupCarriersRes.items)
                setLookupBrigadiers(lookupBrigadiersRes.items)
            } catch (error) {
                dispatch(displayMessage({ message: error, type: 'error' }))
                setLoadingLookupData(false)
            }
            setLoadingLookupData(false)
        }

        getLookupData()
    }, [setLoadingLookupData])

    useEffect(() => {
        getProductQuantities()
    }, [event?.startsAt, event?.endsAt])

    const addEvent = async (afterSaveFunction?: () => void) => {
        if (!event) return
        setLoadingData(true)
        try {
            const eventRes = await EventManager.getManager().createEventAsync({
                ...event,
                products: event.products.map((product) => ({
                    ...product,
                    operatingTime: getTimeSpanFromFormattedTimeSpan(product.operatingTime),
                })),
            })
            if (eventRes) {
                setLoadingData(false)
                navigate(RouteConstants.events + '/' + eventRes.id)
                dispatch(displayMessage({ message: 'Událost přidána', type: 'success' }))
                if (afterSaveFunction) afterSaveFunction()
            }
        } catch (error) {
            setLoadingData(false)
            return dispatch(displayMessage({ message: error, type: 'error' }))
        }
    }

    const getEventsOfProduct = async (productId: string, eventsFrom?: Date, eventsTo?: Date) => {
        if (!event?.products) return
        setAreProductQuantitiesLoading(true)
        try {
            const productEventsRes = await ProductManager.getManager().getEventsOfProductAsync(productId, eventsFrom, eventsTo)
            if (productEventsRes) {
                setAreProductQuantitiesLoading(false)
                return productEventsRes
            }
        } catch (error) {
            dispatch(displayMessage({ message: error, type: 'error' }))
            setAreProductQuantitiesLoading(false)
        }
    }

    const handleSelectContractorChange = (
        newValue: IdNameDto,
        oldId: string,
        index: number,
        sortBy: keyof ContractorOfEventDto | undefined,
        sortOrder: 'asc' | 'desc'
    ) => {
        if (!event) return

        const isContractorInList = event.contractors.some((localContractor) => localContractor.contractorId === newValue.id)
        if (isContractorInList) {
            dispatch(
                displayMessage({
                    message: 'Pracovník je již v přiřazen k této události',
                    type: 'warning',
                })
            )
            return
        }

        const newContractor = contractorList?.find((c) => c.id === newValue.id)

        const brigadiers = event.contractors.filter((contractor) => contractor.contractorType !== ContractorType.Carrier)
        const carriers = event.contractors.filter((contractor) => contractor.contractorType === ContractorType.Carrier)

        if (newContractor?.type === ContractorType.Carrier) {
            const newCarriers = carriers.map((contractor) => {
                if (contractor.contractorId === oldId) {
                    return {
                        ...contractor,
                        contractorId: newValue.id,
                        contractorName: newContractor?.name + ' ' + newContractor?.lastName,
                    }
                }
                return contractor
            })

            setEvent({
                ...event,
                contractors: [...newCarriers, ...brigadiers],
            })
        } else {
            const newBrigadiers = brigadiers
                .sort((a: ContractorOfEventDto, b: ContractorOfEventDto) => {
                    if (sortBy) {
                        if ((a[sortBy] ?? 0) < (b[sortBy] ?? 0)) {
                            return sortOrder === 'asc' ? -1 : 1
                        }
                        if ((a[sortBy] ?? 0) > (b[sortBy] ?? 0)) {
                            return sortOrder === 'asc' ? 1 : -1
                        }
                    }
                    return 0
                })
                .map((contractor, i) => {
                    if (i === index) {
                        return {
                            ...contractor,
                            contractorId: newValue.id,
                            contractorName: newContractor?.name + ' ' + newContractor?.lastName,
                        }
                    }
                    return contractor
                })

            setEvent({
                ...event,
                contractors: [...newBrigadiers, ...carriers],
            })
        }
    }

    const addNewClient = () => {
        const newClientName = event?.description.match(/[Kk]lient: (.*?)\n/g)
        const newClientEmail = event?.description.match(/[eE]mail: (.*?)\n/g)
        const newClientNumber = event?.description.match(/[Cc]islo: (.*?)\n/g)
        const params = new URLSearchParams()
        if (newClientName) params.append('name', newClientName[0].split('lient: ')[1].split('\n')[0])
        if (newClientEmail) params.append('email', newClientEmail[0].split('mail: ')[1].split('\n')[0])
        if (newClientNumber) params.append('phone', newClientNumber[0].split('islo: ')[1].split('\n')[0])
        addEvent(() => navigate(`${RouteConstants.addClient}?${params.toString()}`))
    }

    if (loadingData) return <ScreenLoadingPlaceholder />

    if (!event)
        return (
            <Typography variant='h4' color='error' textAlign='center' marginY={3}>
                Tato událost neexistuje
            </Typography>
        )

    return (
        <CustomContainer>
            <Grid container rowSpacing={4} columnSpacing={4} mb={5}>
                <Grid item xs={12} lg={6}>
                    <EventBasicInformation event={event} setEvent={setEvent} invoice={undefined} onAddNewClient={addNewClient} addHoursToEvent={4} />
                </Grid>

                <Grid item xs={12} lg={6}>
                    <Grid container rowSpacing={3}>
                        <Grid item xs={12}>
                            {(loadingLookupData || loadingData) ? (
                                <CircularProgress />
                            ) : (
                                <ProductTable
                                    event={event}
                                    setEvent={setEvent}
                                    areProductQuantitiesLoading={areProductQuantitiesLoading}
                                    lookUpProducts={lookupProducts ?? []}
                                    getUsedProducts={() => getUsedProducts(event, productQuantities)}
                                    recalculateProductQuantities={() => getProductQuantities()}
                                />
                            )}
                        </Grid>
                        <Grid item xs={12}>
                            {(loadingLookupData || loadingData) ? (
                                <CircularProgress />
                            ) : (
                                <BrigadierTable
                                    event={event}
                                    setEvent={setEvent}
                                    contractorList={
                                        contractorList?.filter(
                                            (contractor: ContractorDto) => contractor.type !== ContractorType.Carrier
                                        ) ?? []
                                    }
                                    isContractorListLoading={loadingTableData}
                                    contractors={lookupBrigadiers ?? []}
                                    handleSelectContractorChange={handleSelectContractorChange}
                                />
                            )}
                        </Grid>
                        <Grid item xs={12}>
                            {(loadingLookupData || loadingData) ? (
                                <CircularProgress />
                            ) : (
                                <CarrierTable
                                    event={event}
                                    setEvent={setEvent}
                                    contractors={lookupCarriers ?? []}
                                    isContractorListLoading={loadingTableData}
                                    contractorList={
                                        contractorList?.filter(
                                            (contractor: ContractorDto) => contractor.type === ContractorType.Carrier
                                        ) ?? []
                                    }
                                    handleSelectContractorChange={handleSelectContractorChange}
                                />
                            )}
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
            <BottomBar onSave={addEvent} defaultRoute={RouteConstants.events} disabled={
                loadingData || loadingTableData || loadingLookupData
            } />
        </CustomContainer>
    )
}

export default AddEvent
